import type {
  Adjustment,
  Invoice,
  InvoiceStatus,
  LineItem,
  PaymentRecord,
} from "./types";

const HKD_FORMATTER = new Intl.NumberFormat("en-HK", {
  style: "currency",
  currency: "HKD",
  currencyDisplay: "code",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const AMOUNT_FORMATTER = new Intl.NumberFormat("en-HK", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatHKD(minor: number): string {
  return HKD_FORMATTER.format(minor / 100);
}

export function formatAmount(minor: number): string {
  return AMOUNT_FORMATTER.format(minor / 100);
}

export function majorToMinor(value: string | number): number {
  const normalized = String(value).replaceAll(",", "").trim();
  if (!normalized) return 0;

  const match = /^(-?)(\d*)(?:\.(\d{0,2}))?$/.exec(normalized);
  if (!match) return 0;

  const sign = match[1] === "-" ? -1 : 1;
  const major = Number.parseInt(match[2] || "0", 10);
  const decimals = (match[3] ?? "").padEnd(2, "0");
  const cents = Number.parseInt(decimals || "0", 10);
  return sign * (major * 100 + cents);
}

function decimalParts(value: number): { numerator: bigint; scale: bigint } {
  if (!Number.isFinite(value) || value < 0) {
    return { numerator: 0n, scale: 1n };
  }

  const normalized = value.toFixed(6).replace(/0+$/, "").replace(/\.$/, "");
  const [whole = "0", fraction = ""] = normalized.split(".");
  const scale = 10n ** BigInt(fraction.length);
  const numerator = BigInt(`${whole}${fraction}`);
  return { numerator, scale };
}

export function calculateLineAmount(rateMinor: number, quantity: number): number {
  const { numerator, scale } = decimalParts(quantity);
  const raw = BigInt(Math.max(0, Math.round(rateMinor))) * numerator;
  return Number((raw + scale / 2n) / scale);
}

export function calculateDiscount(
  subtotalMinor: number,
  adjustment: Adjustment | null,
): number {
  if (!adjustment || subtotalMinor <= 0) return 0;

  if (adjustment.mode === "fixed") {
    return Math.min(
      subtotalMinor,
      Math.max(0, Math.round(adjustment.amountMinor)),
    );
  }

  const multiplier = Math.max(0, Math.min(1, adjustment.multiplier));
  const basisPoints = Math.round(multiplier * 10_000);
  const discountedTotal = Math.round((subtotalMinor * basisPoints) / 10_000);
  return Math.min(subtotalMinor, Math.max(0, subtotalMinor - discountedTotal));
}

export function calculateTotals(
  items: LineItem[],
  adjustment: Adjustment | null,
): {
  items: LineItem[];
  subtotalMinor: number;
  discountMinor: number;
  totalMinor: number;
} {
  const calculatedItems = items.map((item) => ({
    ...item,
    amountMinor: calculateLineAmount(item.rateMinor, item.quantity),
  }));
  const subtotalMinor = calculatedItems.reduce(
    (sum, item) => sum + item.amountMinor,
    0,
  );
  const discountMinor = calculateDiscount(subtotalMinor, adjustment);
  return {
    items: calculatedItems,
    subtotalMinor,
    discountMinor,
    totalMinor: Math.max(0, subtotalMinor - discountMinor),
  };
}

export function calculatePayments(
  totalMinor: number,
  payments: PaymentRecord[],
): {
  paidMinor: number;
  balanceMinor: number;
  overpaidMinor: number;
} {
  const paidMinor = payments.reduce(
    (sum, payment) => sum + Math.max(0, payment.amountMinor),
    0,
  );
  return {
    paidMinor,
    balanceMinor: Math.max(0, totalMinor - paidMinor),
    overpaidMinor: Math.max(0, paidMinor - totalMinor),
  };
}

export function deriveStatus(
  invoice: Pick<
    Invoice,
    "status" | "totalMinor" | "paidMinor" | "dueDate"
  >,
  today = new Date(),
): InvoiceStatus {
  if (invoice.status === "void") return "void";
  if (invoice.paidMinor > 0 && invoice.paidMinor >= invoice.totalMinor) {
    return "paid";
  }
  if (invoice.paidMinor > 0) return "partially_paid";

  const unpaidBaseStatus =
    invoice.status === "paid" || invoice.status === "partially_paid"
      ? "sent"
      : invoice.status;
  const todayIso = toIsoDate(today);
  if (
    unpaidBaseStatus !== "draft" &&
    invoice.dueDate &&
    invoice.dueDate < todayIso
  ) {
    return "overdue";
  }
  return unpaidBaseStatus === "overdue" ? "sent" : unpaidBaseStatus;
}

export function normalizeInvoice(invoice: Invoice): Invoice {
  const totals = calculateTotals(invoice.items, invoice.discount);
  const payments = calculatePayments(totals.totalMinor, invoice.payments);
  const next = {
    ...invoice,
    ...totals,
    ...payments,
  };
  return {
    ...next,
    status: deriveStatus(next),
  };
}

export function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addDays(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return "";
  const next = new Date(year, month - 1, day);
  next.setDate(next.getDate() + days);
  return toIsoDate(next);
}

export function formatDate(isoDate: string): string {
  const [year, month, day] = isoDate.split("-");
  return year && month && day ? `${day}/${month}/${year}` : "—";
}

export function isMeaningfulInvoice(invoice: Invoice): boolean {
  return Boolean(
    invoice.client.name.trim() ||
      invoice.client.contactName.trim() ||
      invoice.purchaseOrderNumber.trim() ||
      invoice.projectReference.trim() ||
      invoice.clientNotes.trim() ||
      invoice.internalNotes.trim() ||
      invoice.receipts.length > 0 ||
      invoice.items.some(
        (item) =>
          item.description.trim() ||
          item.unit.trim() ||
          item.rateMinor > 0 ||
          item.quantity !== 1,
      ),
  );
}

export function nextInvoiceNumber(
  prefix: string,
  year: number,
  existingNumbers: string[],
): string {
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const matcher = new RegExp(`^${escaped}-${year}-(\\d+)$`, "i");
  const highest = existingNumbers.reduce((max, number) => {
    const match = matcher.exec(number);
    return match ? Math.max(max, Number(match[1])) : max;
  }, 0);
  return `${prefix}-${year}-${String(highest + 1).padStart(3, "0")}`;
}

export function statusLabel(status: InvoiceStatus): string {
  return {
    draft: "DRAFT",
    sent: "SENT",
    partially_paid: "PARTIALLY PAID",
    paid: "PAID",
    overdue: "OVERDUE",
    void: "VOID",
  }[status];
}
